import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { t } from '../../i18n';

/**
 * Bild-Lightbox (2026-09-03): Vollbild über allem (fixed inset-0, Portal),
 * Schließen per Klick/X/Escape, Blättern per Pfeilen/Tasten. Gemeinsam für
 * Dart-Room- und Setup-Seite (Marcel: Bilder anklicken → Vollbild).
 */
export interface ImageLightboxProps {
  images: string[];
  /** null = geschlossen */
  index: number | null;
  title?: string;
  onClose: () => void;
  onChange: (index: number) => void;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({ images, index, title = t('web.page.dart-room-detail-page.bild'), onClose, onChange }) => {
  const open = index != null && images[index] != null;
  const count = images.length;

  useEffect(() => {
    if (!open || index == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (count > 1 && e.key === 'ArrowRight') onChange((index + 1) % count);
      if (count > 1 && e.key === 'ArrowLeft') onChange((index - 1 + count) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, index, count, onClose, onChange]);

  if (!open || index == null) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] bg-black/95 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={t('web.component.image-lightbox.bildansicht')}
      data-testid="image-lightbox"
      onClick={onClose}
    >
      <button
        type="button"
        className="absolute top-4 right-4 p-2 rounded-full bg-zinc-800/80 text-white hover:bg-zinc-700"
        aria-label={t('web.component.create-entry-modal.schliessen')}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <X size={20} />
      </button>
      {count > 1 && (
        <>
          <button
            type="button"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-white flex items-center justify-center"
            aria-label={t('web.component.image-lightbox.vorheriges-bild')}
            onClick={(e) => {
              e.stopPropagation();
              onChange((index - 1 + count) % count);
            }}
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-white flex items-center justify-center"
            aria-label={t('web.component.image-lightbox.naechstes-bild')}
            onClick={(e) => {
              e.stopPropagation();
              onChange((index + 1) % count);
            }}
          >
            <ChevronRight size={22} />
          </button>
        </>
      )}
      <img
        src={images[index]}
        alt={t('web.component.image-lightbox.bild', { title, index: index + 1 })}
        className="max-h-[90vh] max-w-[92vw] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
      {count > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-zinc-800/80 text-white text-sm">
          {index + 1} / {count}
        </div>
      )}
    </div>,
    document.body,
  );
};

export default ImageLightbox;
